/* eslint-disable radix */
/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import FeaturedProductItem from "@eisinc/App/Components/Navigation/FeaturedProductItem";
import Button from "@eisinc/MobiusOverrides/Button";
import { MappedLink } from "@eisinc/Overrides/Widgets/Header/MainNavigation";
import { color, xParse, xParserOptions } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface NavigationSubLevelStyles {
    container?: InjectableCss;
    itemsWrapper?: InjectableCss;
    header?: InjectableCss;
    title?: InjectableCss;
    content?: InjectableCss;
    linksWrapper?: InjectableCss;
    linkTitle?: LinkPresentationProps;
    link?: LinkPresentationProps;
    featured?: InjectableCss;
    categoryListWrapper?: InjectableCss;
}
export const NavigationSubLevelStyles: NavigationSubLevelStyles = {
    container: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    itemsWrapper: {
        css: css`
            flex: 1;
            padding: 0 15px;
            overflow-y: auto;
        `,
    },
    header: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
            
            h2 {
                font-size: 20px;
                font-weight: bold;
                margin-bottom: 10px;
            }
        `,
    },
    title: {
        css: css`
            color: ${getColor("text.main")};
        `,
    },
    content: {
        css: css`
            &&,
            && > * {
                font-size: 12px;
                line-height: 14px;
                color: ${getColor("text.main")};
            }
        `,
    },
    linksWrapper: {
        css: css`
            display: grid;
            grid-row-gap: 40px;
            grid-template-columns: repeat(3, minmax(calc(100% / 3), 1fr));
            grid-auto-rows: 40px;
            margin-top: 40px;
        `,
    },
    linkTitle: {
        color: "text.main",
        css: css`
            font-size: 14px;
            font-weight: bold;
            text-transform: uppercase;
            margin-bottom: 10px;
        `,
    },
    link: {
        color: "text.main",
        css: css`
            display: block;
            font-size: 14px;
        `,
    },

    featured: {
        css: css`
            margin-top: -24px;
            margin-bottom: -30px;
            padding: 20px;
            padding-bottom: 10px;
            background: ${color("common.ui3")};
            overflow-y: auto;
            width: 375px;

            h2 {
                font-size: 18px;
                font-weight: bold;
                text-transform: uppercase;
                text-align: center;
                margin-bottom: -10px;
                line-height: 1;
            }
        `,
    },
};

const style = NavigationSubLevelStyles;

const mapStateToProps = (state: ApplicationState) => ({
    state,
});

const mapDispatchToProps = {};

type OwnProps = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> & {
        link: MappedLink;
        closeAllPopups: any;
    };

const parseSpaces = (str: string) => {
    return str.replace("&nbsp;", "").trim();
};

const parseHtml = (html: string) => {
    const tmp = document.createElement("DIV");

    tmp.innerHTML = html;

    return tmp.textContent || tmp.innerText || "";
};

const NavigationSubLevel: React.FC<OwnProps> = ({ link, closeAllPopups  }) => {
    const parseJson = (json: string | undefined): ProductModel[] => (json ? JSON.parse(json) : []);
    const [products, setProducts] = React.useState([] as any);
    const promotionDescription = link.properties?.promoDescription;
    const promotionDescriptionWithoutHtml = promotionDescription && parseHtml(promotionDescription as string);
    const promotionDescriptionWithoutSpaces =
        promotionDescriptionWithoutHtml && parseSpaces(promotionDescriptionWithoutHtml);

    // customization for masonry-like layout on sub-categories
    const resizeGridItem = (item: any) => {
        const grid = document.getElementsByClassName("grid")[0];
        const rowHeight = parseInt(window.getComputedStyle(grid).getPropertyValue("grid-auto-rows"));
        const rowGap = parseInt(window.getComputedStyle(grid).getPropertyValue("grid-row-gap"));
        const rowSpan = Math.ceil(
            (item.querySelector(".content").getBoundingClientRect().height + rowGap) / (rowHeight + rowGap),
        );
        item.style.gridRowEnd = `span ${rowSpan}`;
    };

    React.useEffect(() => {
        resizeAllGridItems();
    });

    const resizeAllGridItems = () => {
        const allItems = document.getElementsByClassName("item");
        let x;
        for (x = 0; x < allItems.length; x++) {
            resizeGridItem(allItems[x]);
        }
    };

    React.useEffect(() => {
        if (link.properties?.featuredProducts) {
            const products = parseJson(link.properties?.featuredProducts);
            setProducts(products);
        } else {
            setProducts([] as any);
        }
    }, [link]);

    return (
        <StyledWrapper {...style.container} key={`navigationSub${link.url}`}>
            <StyledWrapper {...style.itemsWrapper}>
                <StyledWrapper {...style.header}>
                    <Typography {...style.title} as="h2">
                        {link.title}
                    </Typography>
                    <StyledWrapper>
                        <Button
                            {...({ as: "a" } as any)}
                            href={link?.url}
                            sizeVariant="small"
                            css={css`
                                display: block;
                                padding-top: 5px;
                            `}
                        >
                            {translate("Shop All")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>

                <Typography {...style.content} as="p">
                    {promotionDescriptionWithoutSpaces && promotionDescription
                        ? xParse(promotionDescription as string, xParserOptions)
                        : link.htmlContent || link.description
                        ? xParse(link.htmlContent || (link.description as string), xParserOptions)
                        : ""}
                </Typography>

                <StyledWrapper {...style.linksWrapper} className="grid">
                    {link.children
                        ?.filter(item => !item.excludeFromNavigation)
                        .map(item => (
                            <StyledWrapper
                                className="item"
                                {...style.categoryListWrapper}
                                key={`headerLink${item?.url}`}
                            >
                                <StyledWrapper className="content">
                                    <Link href={item?.url} {...style.linkTitle} onClick={closeAllPopups}>
                                        {item.title}
                                    </Link>

                                    {item.children
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .slice(0, 5)
                                        .map(sub => (
                                            <Link
                                                href={sub?.url}
                                                {...style.link}
                                                key={`headerSubLink${sub?.url}`}
                                                onClick={closeAllPopups}
                                            >
                                                {sub.title}
                                            </Link>
                                        ))}
                                    {item.children && item.children.length > 5 && (
                                        <Link
                                            typographyProps={{ size: "14px", lineHeight: "24px" }}
                                            href={item.url}
                                            key={`headerCategoryL2${item.url}`}
                                            onClick={closeAllPopups}
                                        >
                                            {translate("View More")}
                                        </Link>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                </StyledWrapper>
            </StyledWrapper>
            {products.length > 0 && (
                <StyledWrapper {...style.featured}>
                    <Typography {...style.title} as="h2">
                        {translate("Featured Products")}
                    </Typography>
                    {products.map((item: any) => (
                        <FeaturedProductItem key={item.id} product={item} />
                    ))}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(NavigationSubLevel);
